import { useEffect, useCallback } from 'react';

const PerformanceMonitor = () => {
  const sendToAnalytics = useCallback((metric) => {
    // Send metrics to Google Analytics
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', metric.name, {
        event_category: 'Web Vitals',
        event_label: metric.id,
        value: Math.round(metric.value),
        custom_map: { metric_value: metric.value },
        non_interaction: true,
      });
    }

    // Also log to console in development
    if (import.meta.env.DEV) {
      console.log('Web Vital:', metric);
    }
  }, []);

  useEffect(() => {
    let cleanupFunctions = [];

    // Monitor Core Web Vitals
    const loadWebVitals = async () => {
      try {
        // @ts-ignore - web-vitals types are not properly exported
        const webVitals = await import('web-vitals');

        // Check if functions exist and call them
        if (webVitals.getCLS) cleanupFunctions.push(webVitals.getCLS(sendToAnalytics));
        if (webVitals.getFID) cleanupFunctions.push(webVitals.getFID(sendToAnalytics));
        if (webVitals.getFCP) cleanupFunctions.push(webVitals.getFCP(sendToAnalytics));
        if (webVitals.getLCP) cleanupFunctions.push(webVitals.getLCP(sendToAnalytics));
        if (webVitals.getTTFB) cleanupFunctions.push(webVitals.getTTFB(sendToAnalytics));
      } catch (error) {
        console.warn('Failed to load web-vitals:', error);
      }
    };

    loadWebVitals();

    // Monitor navigation timing
    const handleLoad = () => {
      try {
        if (window.performance && window.performance.getEntriesByType) {
          const navigation = window.performance.getEntriesByType('navigation')[0];
          const paint = window.performance.getEntriesByType('paint');

          const metrics = {
            domContentLoaded: navigation ? navigation.domContentLoadedEventEnd - navigation.domContentLoadedEventStart : 0,
            loadComplete: navigation ? navigation.loadEventEnd - navigation.loadEventStart : 0,
            firstPaint: paint ? paint.find(entry => entry.name === 'first-paint')?.startTime : 0,
            firstContentfulPaint: paint ? paint.find(entry => entry.name === 'first-contentful-paint')?.startTime : 0,
          };

          // Send to analytics
          if (typeof window !== 'undefined' && window.gtag) {
            window.gtag('event', 'page_load_metrics', {
              event_category: 'Performance',
              custom_map: {
                dom_content_loaded: metrics.domContentLoaded,
                load_complete: metrics.loadComplete,
                first_paint: metrics.firstPaint,
                first_contentful_paint: metrics.firstContentfulPaint,
              },
            });
          }

          // Log in development
          if (import.meta.env.DEV) {
            console.log('Navigation Timing:', metrics);
          }
        }
      } catch (error) {
        console.warn('Error collecting navigation timing:', error);
      }
    };

    // Monitor errors
    const handleError = (event) => {
      try {
        const errorMessage = event.error?.message || event.message || 'JavaScript Error';

        // Send error to analytics
        if (typeof window !== 'undefined' && window.gtag) {
          window.gtag('event', 'exception', {
            description: errorMessage,
            fatal: false,
          });
        }

        // Log in development
        if (import.meta.env.DEV) {
          console.error('JavaScript Error:', errorMessage);
        }
      } catch (error) {
        console.warn('Error handling JavaScript error:', error);
      }
    };

    // Monitor unhandled promise rejections
    const handleUnhandledRejection = (event) => {
      try {
        const reason = event.reason?.message || event.reason || 'Unhandled Promise Rejection';

        // Send error to analytics
        if (typeof window !== 'undefined' && window.gtag) {
          window.gtag('event', 'exception', {
            description: `Unhandled Promise Rejection: ${reason}`,
            fatal: false,
          });
        }

        // Log in development
        if (import.meta.env.DEV) {
          console.error('Unhandled Promise Rejection:', reason);
        }
      } catch (error) {
        console.warn('Error handling unhandled rejection:', error);
      }
    };

    // Add event listeners
    if (typeof window !== 'undefined') {
      window.addEventListener('load', handleLoad);
      window.addEventListener('error', handleError);
      window.addEventListener('unhandledrejection', handleUnhandledRejection);

      // Store cleanup functions
      cleanupFunctions.push(
        () => window.removeEventListener('load', handleLoad),
        () => window.removeEventListener('error', handleError),
        () => window.removeEventListener('unhandledrejection', handleUnhandledRejection)
      );
    }

    // Cleanup function
    return () => {
      cleanupFunctions.forEach(cleanup => {
        if (typeof cleanup === 'function') {
          cleanup();
        }
      });
    };
  }, [sendToAnalytics]);

  return null;
};

export default PerformanceMonitor;